import React, { useRef } from 'react';
// components
import { AppBar, Box, Paper as MuiPaper, styled } from '@mui/material';

import { useFeature } from '@growthbook/growthbook-react';
import { LinkedLogo } from './modules/LinkedLogo';

import CowbellMainHeader from './CowbellMainHeader';
import AgencyMainHeader from './AgencyMainHeader';
import InsuredMainHeader from './InsuredMainHeader';
import AccountNameHeader, { usesAccountNameHeader } from './AccountNameHeader';
import AgencyDashboardHeader from './AgencyDashboardHeader';

// other
import { useActor } from '../../../../components/hooks/useActor';
import { usePersona } from '../../../../components/hooks/usePersona';
import { useAgencyDashboardFeature } from '../../../dashboard/agency/agency-dashboard.statics';
import { useAgencyShell } from '../../../../personas/broker/useAgencyShell';
import Showable from '../../../../components/Showable';
import { SwitchAccountsMenu } from './helpers';
import { DashboardOptInOutModule } from '../../../dashboard/agency/modules/DashboardOptInOutModule';
import { usePlatformLanguage } from '../../../../utils/hooks/usePlatformLanguage';
import { languages } from '../../../../i18n/i18n.language-config';
import { FEATURE_FLAG_KEYS } from '../../../../config/growthbook';
import { MOO_V2_TOGGLE_EVENT } from '../../../../components/ask-moo/v2/AskMooIntegrationV2';
import { useAskMooEnabled } from '../../../../components/ask-moo/hooks/useAskMooEnabled';
import { AskMooLauncherButton } from '../../../../components/ask-moo/v2/AskMooLauncherButton';
import { usePublishHeaderHeight } from './use-publish-header-height';
import { HEADER_END_INSET, HEADER_ITEM_GAP } from './HeaderItems';

export function MainHeader() {
  const actor = useActor();
  const { account } = actor;
  const persona = usePersona();
  const agencyDashboardFeature = useAgencyDashboardFeature();
  const AGENCY_DASHBOARD_PREREQ = useFeature(
    FEATURE_FLAG_KEYS.AGENCY_DASHBOARD_PREREQ
  );
  const region = usePlatformLanguage();
  const askMooEnabled = useAskMooEnabled();
  const headerRef = useRef<HTMLElement>(null);
  usePublishHeaderHeight(headerRef);
  const shell = useAgencyShell();
  const agencyDashboard = persona.isAgency && shell.header === 'dashboard';
  const launcher = askMooEnabled ? (
    <AskMooLauncherButton
      onToggle={() => window.dispatchEvent(new Event(MOO_V2_TOGGLE_EVENT))}
    />
  ) : null;

  return (
    <AppBar
      ref={headerRef}
      color="transparent"
      sx={{
        left: 0,
        right: 'var(--ask-moo-sidebar-width, 0px)',
        width: 'auto',
        transition: 'right 0.2s ease',
        // Below the bar only. The default elevation spread sideways past its right edge and
        // drew a seam against the page scrollbar, which is painted the header's colour beside it.
        boxShadow: '0 0.375rem 0.375rem -0.375rem rgba(0, 0, 0, 0.25)',
      }}
    >
      <Paper elevation={0} className="flex" style={{ position: 'relative' }}>
        <Showable show={!agencyDashboard}>
          <LinkedLogo />
          <Box width="20%" display="flex" alignItems="center">
            <SwitchAccountsMenu />
          </Box>
        </Showable>

        <HeaderContainer agencyDashboard={agencyDashboard}>
          <CowbellMainHeader show={persona.isCowbell} account={account} />
          <AgencyDashboardHeader
            show={agencyDashboard}
            megaMenu={shell.megaMenu}
            fullWidth={shell.sidebar === 'rail'}
            withAgency={shell.dashboard === 'v2'}
            home={shell.dashboard !== 'v2'}
            account={account}
            trailing={launcher}
          />
          <AgencyMainHeader
            show={persona.isAgency && !agencyDashboard}
            account={account}
          />
          <InsuredMainHeader show={persona.isInsured} account={account} />
          <AccountNameHeader
            show={usesAccountNameHeader(persona.type)}
            account={account}
          />
        </HeaderContainer>

        {/* The agency dashboard's header carries the launcher beside its avatar, inside its own width; an empty box here
            would still take the leftover space and push that header off centre. */}
        {/* The persona header shrinks to fit (logo + 20% + 80% overflows the row), so `ml: auto` gets no leftover
            space and the launcher would sit flush against the avatar. The padding gives it the same gap the persona
            header puts between its own items. */}
        {!agencyDashboard && launcher && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              flexShrink: 0,
              ml: 'auto',
              mr: HEADER_END_INSET,
              pl: HEADER_ITEM_GAP,
            }}
          >
            {launcher}
          </Box>
        )}

        <Showable
          show={
            persona.isAgency &&
            AGENCY_DASHBOARD_PREREQ.on &&
            region === languages['en-US'] &&
            shell.dashboard !== 'v2'
          }
        >
          <DashboardOptInOutModule enabled={agencyDashboardFeature.on} />
        </Showable>
      </Paper>
    </AppBar>
  );
}

const HeaderContainer = ({
  children,
  agencyDashboard,
}: Readonly<{
  children: React.ReactElement[];
  agencyDashboard: boolean;
}>) => {
  if (agencyDashboard) {
    return <>{children}</>;
  }

  return (
    // A column so the persona's header keeps the full width, centred on the
    // header's vertical middle like the logo and the Ask Moo button.
    <Box
      width="80%"
      paddingLeft={2}
      display="flex"
      flexDirection="column"
      justifyContent="center"
    >
      {children}
    </Box>
  );
};

// A full-width bar, square at both ends: the theme's Paper radius rounded its right end just
// short of the window edge, so the header looked as if it stopped before it.
const Paper = styled(MuiPaper)({
  padding: '.75rem',
  paddingLeft: '1.25rem',
  borderRadius: 0,
});
